import type { Sport } from './index';

export type BookId = string;
export type BookKind = 'sportsbook' | 'dfs' | 'exchange';
/** What a book's limit means: a sportsbook's max bet, or an exchange's available liquidity. */
export type LimitKind = 'max_bet' | 'liquidity';

export interface Book {
  id: BookId;
  name: string;
  kind: BookKind;
  limitKind: LimitKind | null;
  dfs?: { impliedOdds?: number; entryLabel: string };
  isActive: boolean;
  displayOrder: number;
  /** The sports whose odds feed carries this book: a board shows a column only for a book its feed carries. */
  sports: readonly Sport[];
}

const EVERY_SPORT: readonly Sport[] = ['nba', 'mlb'];

/** A book as `/api/books` sent it: one sent before `sports` existed (a cached payload mid-deploy) is read as carried by every sport. */
export function bookFromWire(book: Omit<Book, 'sports'> & { sports?: readonly Sport[] }): Book {
  return { ...book, sports: book.sports ?? EVERY_SPORT };
}

export type BookCatalog = ReadonlyMap<BookId, Book>;

export function catalogFrom(books: readonly Book[]): BookCatalog {
  return new Map(books.map((book) => [book.id, book]));
}

export function activeBooksInOrder(catalog: BookCatalog): Book[] {
  return [...catalog.values()]
    .filter((book) => book.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/** The active books the sport's feed carries, in display order; without a sport, those some feed carries. */
export function carriedBooksInOrder(catalog: BookCatalog, sport?: Sport): Book[] {
  return activeBooksInOrder(catalog).filter((book) => (sport ? book.sports.includes(sport) : book.sports.length > 0));
}

export function isDfsBook(id: BookId, catalog: BookCatalog): boolean {
  return catalog.get(id)?.kind === 'dfs';
}

export function dfsBookIds(catalog: BookCatalog): ReadonlySet<BookId> {
  return new Set([...catalog.values()].filter((book) => book.kind === 'dfs').map((book) => book.id));
}
